<script setup lang="ts">
import type { Chart } from 'highcharts';
import { toRef } from 'vue';
import { RotateCcw } from 'lucide-vue-next';
import GenericChart from '@/components/charts/GenericChart.vue';
import { Button } from '@/components/ui/button';
import type { GroupedWeeklySeries } from '@/composables/records/useGroupedWeeklyAggregation';
import { useGroupedWeeklyChartOptions } from '@/composables/records/useGroupedWeeklyChartOptions';
import { clearHoverBand } from '@/composables/records/useRecordsChartOptions';

/*
 * The multi-series counterpart of `RecordsWeeklyChart`: one bar per series per week, grouped side
 * by side, where that component draws exactly one series. Click-to-select-a-week, fade-on-selection
 * and the hover band behave the same on both, and both resolve them in their options composable.
 */
const props = defineProps<{
    series: GroupedWeeklySeries[];
    selectedWeekStart: string | null;
    /** The colours the series take, in the order they are drawn. See `GroupedWeeklyChartConfig`. */
    colors: string[];
    /** What the bars count, in the page's own words - carried in the chart's own title. */
    title: string;
    /** What a series is, as the prefix of its Highcharts id. See `GroupedWeeklyChartConfig`. */
    seriesIdPrefix: string;
    /** More about one series' week, after its value in the tooltip. See `GroupedWeeklyChartConfig`. */
    pointDetail?: (weekStart: string, seriesId: number) => string | null;
}>();

const emit = defineEmits<{
    'week-selected': [weekStart: string];
    'selection-reset': [];
}>();

const options = useGroupedWeeklyChartOptions(toRef(props, 'series'), toRef(props, 'selectedWeekStart'), {
    colors: props.colors,
    title: toRef(props, 'title'),
    seriesIdPrefix: props.seriesIdPrefix,
    pointDetail: (weekStart: string, seriesId: number) => props.pointDetail?.(weekStart, seriesId) ?? null,
    onBinClicked: (weekStart: string) => emit('week-selected', weekStart),
});

function onChartUpdate(instance: Chart) {
    // A redraw drops imperatively added bands; the next pointer move puts a fresh one back.
    clearHoverBand(instance);
}
</script>

<template>
    <!-- GenericChart declares `options` as a ComputedRef, which the template unwraps before it is
         checked; every chart in the app casts here for the same reason. -->
    <GenericChart :options="(options as any)" @update="onChartUpdate">
        <template #prepend-actions>
            <!-- The page's own controls, such as the series dropdown: this component only knows the
                 chart, not the selection driving which series it draws. -->
            <slot name="prepend-actions" />
            <Button variant="outline" size="sm" :disabled="selectedWeekStart === null" :aria-label="$t('views.pigs.records.chart.resetSelectionHint')" @click="emit('selection-reset')">
                <RotateCcw class="size-4 mr-1.5" aria-hidden="true" />
                {{ $t('views.pigs.records.chart.resetSelection') }}
            </Button>
        </template>
    </GenericChart>
</template>
